import { useEffect, useRef, useState } from "react";
import "./bop030w020.css";
import { useNavigate } from "react-router-dom";
import BOP030W020Template from "./bop030w020.template";
import useApiRequest from "../../common/useApiRequest";
import { SYSTEM_ERROR } from "../../common/apiError";

const MAX_QUESTION_LENGTH = 300;

export const MODEL_OPTIONS = [
  { value: "", label: "デフォルト" },
  { value: "claude-haiku-4-5", label: "Claude Haiku 4.5（高速・低コスト）" },
  { value: "claude-sonnet-5", label: "Claude Sonnet 5（バランス）" },
  { value: "claude-opus-4-8", label: "Claude Opus 4.8（高性能）" },
];

interface AskResponse {
  answer: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface HistoryResponse {
  id: number;
  detailModeFlg: number;
  startMonth: string;
  endMonth: string | null;
  model: string;
  question: string;
  answer: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  registerDt: string;
}

type Phase = "idle" | "submitting" | "answered" | "failed";

/**
 * 機能ID: BOP030W020
 * 機能名: AI家計相談画面
 */
const BOP030W020 = () => {
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const [detailMode, setDetailMode] = useState(false);
  const [targetMonth, setTargetMonth] = useState(getCurrentMonth);
  const [startMonth, setStartMonth] = useState(getCurrentMonth);
  const [endMonth, setEndMonth] = useState(getCurrentMonth);
  const [question, setQuestion] = useState("");
  const [model, setModel] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [answer, setAnswer] = useState<string | null>(null);
  const [usedModel, setUsedModel] = useState<string | null>(null);
  const [usedInputTokens, setUsedInputTokens] = useState<number | null>(null);
  const [usedOutputTokens, setUsedOutputTokens] = useState<number | null>(null);
  const [usedCostUsd, setUsedCostUsd] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [historyMonthFrom, setHistoryMonthFrom] = useState("");
  const [historyMonthTo, setHistoryMonthTo] = useState("");
  const [historyResults, setHistoryResults] = useState<HistoryResponse[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyErrorMessage, setHistoryErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { execute } = useApiRequest<AskResponse>();
  const { execute: executeHistory } = useApiRequest<HistoryResponse[]>();
  // マウント時の初回検索・条件変更検索・ask成功後の再検索が競合し得るため、
  // 呼び出しごとに採番して「最後に発行したリクエスト」の結果だけを状態に反映する
  const historyRequestIdRef = useRef(0);

  const searchHistory = async (monthFrom: string, monthTo: string) => {
    const requestId = ++historyRequestIdRef.current;
    const isLatest = () => requestId === historyRequestIdRef.current;

    setIsHistoryLoading(true);
    const result = await executeHistory(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs02001/history`,
      {
        method: "POST",
        body: JSON.stringify({
          targetMonthFrom: monthFrom ? monthFrom.replace("-", "") : null,
          targetMonthTo: monthTo ? monthTo.replace("-", "") : null,
        }),
      },
    );

    if (!isLatest()) return;
    setIsHistoryLoading(false);

    if (!result.ok) {
      setHistoryErrorMessage(result.error.message);
      return;
    }

    setHistoryResults(Array.isArray(result.data) ? result.data : []);
    setHistoryErrorMessage(null);
  };

  // 画面表示時に、絞り込みなし（全件）で履歴を取得しておく
  useEffect(() => {
    searchHistory("", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHistorySearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchHistory(historyMonthFrom, historyMonthTo);
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const isPeriodValid = detailMode
    ? !!targetMonth
    : !!startMonth && !!endMonth && startMonth <= endMonth;

  const isSubmitDisabled =
    phase === "submitting" ||
    !isPeriodValid ||
    question.trim().length === 0 ||
    question.length > MAX_QUESTION_LENGTH;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    setPhase("submitting");

    const result = await execute(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs02001/ask`,
      {
        method: "POST",
        body: JSON.stringify(
          detailMode
            ? {
                detailMode: true,
                targetMonth: targetMonth.replace("-", ""),
                question: question.trim(),
                model: model || undefined,
              }
            : {
                detailMode: false,
                startMonth: startMonth.replace("-", ""),
                endMonth: endMonth.replace("-", ""),
                question: question.trim(),
                model: model || undefined,
              },
        ),
      },
    );

    if (!result.ok) {
      setPhase("failed");
      setErrorMessage(result.error.message);
      setAnswer(null);
      setUsedModel(null);
      setUsedInputTokens(null);
      setUsedOutputTokens(null);
      setUsedCostUsd(null);
      return;
    }

    if (typeof result.data?.answer !== "string") {
      setPhase("failed");
      setErrorMessage(SYSTEM_ERROR.message);
      setAnswer(null);
      setUsedModel(null);
      setUsedInputTokens(null);
      setUsedOutputTokens(null);
      setUsedCostUsd(null);
      return;
    }

    setPhase("answered");
    setAnswer(result.data.answer);
    setUsedModel(result.data.model ?? null);
    setUsedInputTokens(typeof result.data.inputTokens === "number" ? result.data.inputTokens : null);
    setUsedOutputTokens(typeof result.data.outputTokens === "number" ? result.data.outputTokens : null);
    setUsedCostUsd(typeof result.data.costUsd === "number" ? result.data.costUsd : null);
    setErrorMessage(null);

    // バックエンド側で回答表示前に履歴テーブルへ保存済みのため、現在の検索条件で再取得して一覧に反映する
    searchHistory(historyMonthFrom, historyMonthTo);
  };

  const displayStatus = phase === "submitting" ? "loading" : phase;

  return (
    <BOP030W020Template
      detailMode={detailMode}
      targetMonth={targetMonth}
      startMonth={startMonth}
      endMonth={endMonth}
      question={question}
      model={model}
      displayStatus={displayStatus}
      answer={answer}
      usedModel={usedModel}
      usedInputTokens={usedInputTokens}
      usedOutputTokens={usedOutputTokens}
      usedCostUsd={usedCostUsd}
      errorMessage={errorMessage}
      isSubmitDisabled={isSubmitDisabled}
      maxQuestionLength={MAX_QUESTION_LENGTH}
      onDetailModeChange={setDetailMode}
      onTargetMonthChange={setTargetMonth}
      onStartMonthChange={setStartMonth}
      onEndMonthChange={setEndMonth}
      onQuestionChange={setQuestion}
      onModelChange={setModel}
      onSubmit={handleSubmit}
      onBack={handleBack}
      historyMonthFrom={historyMonthFrom}
      historyMonthTo={historyMonthTo}
      historyResults={historyResults}
      isHistoryLoading={isHistoryLoading}
      historyErrorMessage={historyErrorMessage}
      onHistoryMonthFromChange={setHistoryMonthFrom}
      onHistoryMonthToChange={setHistoryMonthTo}
      onHistorySearch={handleHistorySearch}
    />
  );
};

export default BOP030W020;
